import React, { useCallback, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import { MdSearch } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchManufacturerRetailers } from '../../../../services/Http/ManufacturerApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const RetailersTable = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: 10, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const loadRetailers = useCallback(async ({ page = 1, perPage = 10, searchTerm = '' } = {}) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerRetailers({
                page,
                perPage,
                search: searchTerm,
            });
            setRecords(response.data || []);
            setMeta(response.meta || { total: 0, per_page: perPage, current_page: page });
        } catch (error) {
            toast.error(t('ManufacturerRetailers.errors.loadList'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadRetailers();
    }, [loadRetailers]);

    const handleSearch = (event) => {
        event.preventDefault();
        loadRetailers({ page: 1, perPage: meta.per_page, searchTerm: search });
    };

    const columns = [
        {
            name: t('ManufacturerRetailers.table.columns.retailer'),
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.table.columns.inventory'),
            selector: (row) => row.item_count,
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.table.columns.sold'),
            selector: (row) => row.sold_count,
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.table.columns.sellInValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_in_value,
                localAmount: row.sell_in_value_local ?? row.sell_in_local,
                localCurrencyId: row.sell_in_currency_id ?? row.currency_id,
                localCurrencyCode: row.sell_in_currency_code ?? row.currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.table.columns.sellThroughValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_through_value,
                localAmount: row.sell_through_value_local ?? row.sell_through_local,
                localCurrencyId: row.sell_through_currency_id ?? row.currency_id,
                localCurrencyCode: row.sell_through_currency_code ?? row.currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.table.columns.venues'),
            selector: (row) => row.venues_count,
            sortable: true,
        },
        {
            name: t('ManufacturerRetailers.table.columns.action'),
            cell: (row) => (
                <ActionButton onClick={() => navigate(`/retailers/${row.id}`)}>
                    {t('ManufacturerRetailers.table.view')}
                </ActionButton>
            ),
        },
    ];

    return (
        <>
            <Toolbar>
                <SearchForm onSubmit={handleSearch}>
                    <MdSearch />
                    <input
                        type="text"
                        placeholder={t('ManufacturerRetailers.table.search')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </SearchForm>
            </Toolbar>
            <DataTable
                columns={columns}
                data={records}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={meta.total}
                paginationPerPage={meta.per_page}
                onChangePage={(page) => loadRetailers({ page, perPage: meta.per_page, searchTerm: search })}
                onChangeRowsPerPage={(perPage, page) => loadRetailers({ page, perPage, searchTerm: search })}
                highlightOnHover
                persistTableHead
            />
        </>
    );
};

export default RetailersTable;

const Toolbar = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
`;

const SearchForm = styled.form`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(130, 22, 146, 0.05);
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 8px;
    padding: 0.375rem 0.75rem;

    input {
        border: none;
        background: transparent;
        outline: none;
        width: 220px;
    }

    svg {
        color: rgba(130, 22, 146, 1);
    }
`;

const ActionButton = styled.button`
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    border: 1px solid rgba(130, 22, 146, 0.4);
    background: transparent;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(130, 22, 146, 0.08);
    }
`;
